import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleChat, closeChat, goBackToRoomList, openChatRoom } from '../redux/action/chatSlice';
import api from '../utils/api';
import toast from 'react-hot-toast';

const ChatWidget = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { isOpen, activeRoomId, activeStore } = useSelector((state) => state.chat);

  const [rooms, setRooms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      if (!activeRoomId && !activeStore) {
        fetchRooms();
      } else if (activeRoomId) {
        fetchMessages(activeRoomId);
      }
    }
  }, [isOpen, activeRoomId, activeStore, isAuthenticated]);

  useEffect(() => {
    // Auto scroll down when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchRooms = async () => {
    setLoadingRooms(true);
    try {
      const res = await api.get('/api/chat/rooms');
      setRooms(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch rooms', err);
    } finally {
      setLoadingRooms(false);
    }
  };

  const fetchMessages = async (roomId) => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/api/chat/rooms/${roomId}/messages`);
      setMessages(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch messages', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setIsSending(true);
    try {
      const payload = { message: newMessage };
      if (activeRoomId) {
        payload.room_id = activeRoomId;
      } else if (activeStore) {
        payload.store_id = activeStore.id;
      }

      const res = await api.post('/api/chat/messages', payload);
      const newMsg = res.data.data;
      
      // If was starting a new chat with a store
      if (!activeRoomId && res.data.data.chat_room_id) {
        // We now have a room id natively inside the message or response? Wait, standard is response.data.data has room_id
        dispatch(openChatRoom(newMsg.chat_room_id));
      } else {
        setMessages([...messages, newMsg]);
      }
      setNewMessage('');
    } catch (err) {
      toast.error('Gagal mengirim pesan');
    } finally {
      setIsSending(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => dispatch(toggleChat())}
          className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-2xl hover:bg-emerald-400 hover:scale-105 active:scale-95 transition-all outline-none ring-4 ring-emerald-500/30"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* Widget Panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col w-[350px] sm:w-[380px] h-[500px] bg-zinc-900 border border-white/10 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.5)] overflow-hidden font-sans">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-800/50 border-b border-white/10">
        <div className="flex items-center gap-3">
          {(activeRoomId || activeStore) && (
            <button
              onClick={() => dispatch(goBackToRoomList())}
              className="text-zinc-400 hover:text-white transition"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <h3 className="font-bold text-white">
            {activeStore ? activeStore.name : activeRoomId ? 'Chat Room' : 'Live Chat'}
          </h3>
        </div>
        <button
          onClick={() => dispatch(closeChat())}
          className="text-zinc-400 hover:text-white transition p-1 rounded-lg hover:bg-white/5"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto bg-zinc-950/50 p-4 scrollbar-hide flex flex-col relative w-full h-full">
        {!activeRoomId && !activeStore ? (
          /* Room List */
          loadingRooms ? (
            <div className="flex items-center justify-center flex-1">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : rooms.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-center px-4 space-y-3">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
                <svg className="w-8 h-8 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-white">Belum ada obrolan</p>
              <p className="text-xs text-zinc-400">Pesan dari penjual akan muncul di sini. Mulai obrolan dari halaman produk!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rooms.map(room => (
                <button
                  key={room.id}
                  onClick={() => dispatch(openChatRoom(room.id))}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition text-left group"
                >
                  <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center font-bold text-indigo-400 shrink-0 border border-indigo-500/20">
                    {room.store?.name?.charAt(0) || 'S'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-bold text-white truncate group-hover:text-emerald-400 transition">{room.store?.name || 'Toko'}</p>
                      {room.unread_count > 0 && (
                        <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                          {room.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-400 truncate">
                      {room.latest_message ? `[${room.latest_message.sender?.name}] ${room.latest_message.message}` : 'Belum ada pesan'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          /* Message List */
          loadingMessages ? (
            <div className="flex items-center justify-center flex-1 h-full">
              <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
           <div className="flex flex-col gap-3 flex-1 h-full">
               <div className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-hide">
                 {messages.map((msg, idx) => {
                   const isMe = msg.sender_id === user.id;
                   return (
                     <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                       <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm ${isMe ? 'bg-emerald-500 text-white rounded-tr-sm' : 'bg-zinc-800 text-zinc-200 rounded-tl-sm border border-white/5'}`}>
                         <p className="whitespace-pre-wrap word-break-all leading-relaxed">{msg.message}</p>
                         <span className="text-[10px] opacity-70 mt-1 block text-right font-medium">
                           {new Date(msg.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                         </span>
                       </div>
                     </div>
                   );
                 })}
                 {(messages.length === 0 && activeStore) && (
                   <div className="flex flex-col items-center justify-center h-full text-zinc-500 text-sm space-y-2 mt-10">
                     <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-2">
                       <span className="text-xl">👋</span>
                     </div>
                     <p>Kirim pesan pertama ke {activeStore.name}</p>
                   </div>
                 )}
                 <div ref={messagesEndRef} />
               </div>
            </div>
          )
        )}
      </div>

      {/* Input Area */}
      {(activeRoomId || activeStore) && (
        <form onSubmit={handleSendMessage} className="p-3 bg-zinc-800/80 backdrop-blur-md border-t border-white/10 flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Tulis pesan..."
            className="flex-1 bg-zinc-950 border border-zinc-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all placeholder:text-zinc-500"
          />
          <button
            type="submit"
            disabled={isSending || !newMessage.trim()}
            className="bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white w-10 shrink-0 rounded-xl transition-all flex items-center justify-center aspect-square shadow-sm"
          >
            {isSending ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <svg className="w-4 h-4 translate-x-px" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
              </svg>
            )}
          </button>
        </form>
      )}
      </div>
     )}
    </>
  );
};

export default ChatWidget;
